import { useFocusEffect, useRoute } from '@react-navigation/native';
import { useCallback, useRef, useState } from 'react';
import { useHandleNavigationContextHeader } from '@v2/pages/common/hooks/useHandleNavigationContextHeader';
import { JobContentWrapperProps } from '@v2/pages/job/common/components/JobContentWrapper';
import { parseRouteParamAsNumber } from '@helpers';

export interface UseManageJobContentWrapperParams
  extends Pick<
    JobContentWrapperProps,
    'productionOrdersSearch' | 'operationsSearch' | 'setProductionOrders' | 'setProductionOrderId' | 'productionOrders' | 'setProductionOrderJobsList'
  > {}

export const useManageJobContentWrapper = ({
  productionOrdersSearch,
  operationsSearch,
  setProductionOrders,
  setProductionOrderId,
  productionOrders,
  setProductionOrderJobsList,
}: UseManageJobContentWrapperParams) => {
  const productionOrder = productionOrders?.data?.[0];

  const route = useRoute();
  const productionOrderId = parseRouteParamAsNumber(route.params?.productionOrderId);
  const showList = route.params?.keepList;

  const { activeLevelSubScreenName } = useHandleNavigationContextHeader();

  const [productionOrderDataLoading, setProductionOrderDataLoading] = useState<boolean>(false);

  const jobsAbortController = useRef<AbortController | null>(null);

  const jobsApiCall = () => {
    if (!jobsAbortController.current) {
      jobsAbortController.current = new AbortController();
    }

    return operationsSearch({
      data: { orderBy: ['SortOrder'], search: '*' },
      filter: { oDataFilter: `ProductionOrderId eq ${productionOrderId}` },
      select: [],
      resetHistory: true,
      ignoreHistory: true,
      signal: jobsAbortController.current.signal,
    });
  };

  useFocusEffect(
    useCallback(() => {
      (async () => {
        if (productionOrderId == null || productionOrdersSearch == null || productionOrder?.productionOrderId === productionOrderId) {
          setProductionOrderDataLoading(false);
          return;
        }
        const result = await productionOrdersSearch({
          data: {},
          filter: { oDataFilter: `ProductionOrderId eq ${productionOrderId}` },
          select: [],
          ignoreHistory: true,
          resetHistory: true,
        });
        if (result.searchResult) {
          setProductionOrders(result.searchResult);
          if (result.searchResult.length === 1) {
            setProductionOrderId(result.searchResult?.[0]?.productionOrderId);
          }
        }
        setProductionOrderDataLoading(false);
        return result;
      })();
    }, [productionOrderId, productionOrder?.productionOrderId])
  );

  useFocusEffect(
    useCallback(() => {
      if (productionOrderId == null) return;

      jobsAbortController.current?.abort();
      jobsAbortController.current = new AbortController();

      (async () => {
        try {
          const result = await jobsApiCall();

          if (jobsAbortController.current?.signal.aborted) return;

          if (result?.searchResult) {
            setProductionOrderJobsList(result.searchResult);
          }
        } catch (e) {
          if (e instanceof Error && e.name === 'AbortError') {
            return;
          }
          console.error(e);
        }
      })();

      return () => {
        jobsAbortController.current?.abort();
      };
    }, [productionOrderId])
  );

  return {
    productionOrder,
    productionOrderDataLoading,
    showList,
    activeLevelSubScreenName,
  };
};
